import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import {
  FormControl,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabaseClient } from "src/lib/supabase/supabaseClient";
import EditModal from "src/shared/components/EditModal";
import { MuiOtpInput } from "mui-one-time-password-input";

type EditPasswordModalTypes = {
  openModal: boolean;
  setOpenModal: (o: boolean) => void;
};

const EditPasswordModal = ({
  openModal,
  setOpenModal,
}: EditPasswordModalTypes) => {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [requireOTP, setRequireOTP] = useState(false);
  const [otp, setOTP] = useState("");
  const [passwordVisibility, setPasswordVisibility] = useState<boolean>(false);
  const [passwordConfirmationVisibility, setPasswordConfirmationVisibility] =
    useState<boolean>(false);
  const page = "account.overview.passwordCard";
  const { t } = useTranslation();

  const handleOTP = useCallback((value: string) => {
    setOTP(value);
  }, []);

  const handleOnCancel = useCallback(() => {
    setPassword("");
    setConfirmPassword("");
    setRequireOTP(false);
    setOTP("");
    setError("");
    setOpenModal(false);
  }, [setOpenModal]);

  const handleOnSubmit = useCallback(async () => {
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (trimmedPassword != trimmedConfirmPassword) {
      setError(
        t(
          `${page}.incorrect_password`,
          "Looks like one of the passwords is incorrect",
        ),
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data, error } = await supabaseClient.auth.updateUser({
        password: trimmedPassword,
        ...(otp && { nonce: otp }),
      });

      if (error?.code === "reauthentication_needed") {
        await supabaseClient.auth.reauthenticate();
        setRequireOTP(true);
        return;
      }

      if (error != null) {
        setError(error.message);
        return;
      }

      if (data.user != null) {
        setPassword("");
        setConfirmPassword("");
        setOpenModal(false);
        setRequireOTP(false);
        setOTP("");
      }
    } catch (err) {
      console.error("Unexpected error", err);
    } finally {
      setLoading(false);
    }
  }, [confirmPassword, otp, password, setOpenModal, t]);

  return (
    <EditModal
      open={openModal}
      onClose={handleOnCancel}
      title={t(`${page}.modalHeader`)}
      subTitle={t(`${page}.subtext`)}
      loading={loading}
      onSubmit={handleOnSubmit}
    >
      <Stack gap={3}>
        <FormControl>
          <TextField
            type={passwordVisibility ? "text" : "password"}
            placeholder={t(`${page}.new_password`)}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setPasswordVisibility((o) => !o)}
                      onMouseDown={() => setPasswordVisibility((o) => !o)}
                    >
                      {passwordVisibility ? (
                        <VisibilityOutlined />
                      ) : (
                        <VisibilityOffOutlined />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </FormControl>
        <FormControl>
          <TextField
            type={passwordConfirmationVisibility ? "text" : "password"}
            placeholder={t(`${page}.confirm_password`)}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() =>
                        setPasswordConfirmationVisibility((o) => !o)
                      }
                      onMouseDown={() =>
                        setPasswordConfirmationVisibility((o) => !o)
                      }
                    >
                      {passwordConfirmationVisibility ? (
                        <VisibilityOutlined />
                      ) : (
                        <VisibilityOffOutlined />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </FormControl>
        {requireOTP ? (
          <Stack gap={2} alignItems="center">
            <Typography variant="h6">
              {t(`${page}.one_time_password`, "Check your email for OTP")}
            </Typography>
            <MuiOtpInput length={6} value={otp} onChange={handleOTP} />
          </Stack>
        ) : null}
        {error ? (
          <Typography sx={{ color: "red" }} textAlign="center">
            {error}
          </Typography>
        ) : null}
      </Stack>
    </EditModal>
  );
};

export default EditPasswordModal;
